import { View } from "react-native";
import { BrownButton, TextActionButton } from "@louise/moon-ui";
import { colors } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import CoverageCardSurface from "./CoverageCardSurface";
import TexturedCoverageCard from "./TexturedCoverageCard";

type Props = {
  hasEmployerBenefits: boolean | null;
  employerBenefit: string | null;
  /** Dev-only comparison treatment; the live screen remains flat for now. */
  visualStyle?: "flat" | "medical-history";
  /** Space after the card when another element follows it. */
  bottomSpacing?: number;
  onPress: () => void;
};

export default function EmployerBenefitsCard({
  hasEmployerBenefits,
  employerBenefit,
  visualStyle = "flat",
  bottomSpacing = 24,
  onPress,
}: Props) {
  const t = useTranslations();
  const benefitOnFile = hasEmployerBenefits === true;
  const noBenefit = hasEmployerBenefits === false;
  const status = benefitOnFile
    ? t("you.coverage_benefits.employer_benefits_card_on_file")
    : noBenefit
      ? t("you.coverage_benefits.employer_benefits_card_none_subtitle")
      : t("you.coverage_benefits.employer_benefits_card_not_on_file");
  const title = benefitOnFile
    ? (employerBenefit ??
      t("you.coverage_benefits.employer_benefits_card_saved_title"))
    : noBenefit
      ? t("you.coverage_benefits.employer_benefits_card_none_title")
      : t("you.coverage_benefits.employer_benefits_card_empty_card_title");
  const subtitle = benefitOnFile
    ? t("you.coverage_benefits.employer_benefits_card_saved_description")
    : noBenefit
      ? t("you.coverage_benefits.employer_benefits_card_none_description")
      : t("you.coverage_benefits.employer_benefits_card_empty_card_subtitle");
  const fields: [
    { label: string; value: string },
    { label: string; value: string },
  ] = benefitOnFile
    ? [
        {
          label: t("you.coverage_benefits.employer_benefits_card_program"),
          value:
            employerBenefit ??
            t("you.coverage_benefits.employer_benefits_card_not_on_file"),
        },
        {
          label: t("you.coverage_benefits.employer_benefits_card_status"),
          value: t("you.coverage_benefits.employer_benefits_card_on_file"),
        },
      ]
    : [
        {
          label: t("you.coverage_benefits.employer_benefits_card_program"),
          value: t("you.coverage_benefits.employer_benefits_card_not_added"),
        },
        {
          label: t("you.coverage_benefits.employer_benefits_card_status"),
          value: noBenefit
            ? t("you.coverage_benefits.employer_benefits_card_none_subtitle")
            : t("you.coverage_benefits.employer_benefits_card_optional"),
        },
      ];
  const actionLabel = benefitOnFile
    ? t("you.insurance_modal.edit")
    : noBenefit
      ? t("you.coverage_benefits.employer_benefits_card_update")
      : t("you.coverage_benefits.employer_benefits_card_action");

  return (
    <View style={{ marginBottom: bottomSpacing, gap: 8 }}>
      {visualStyle === "medical-history" ? (
        <TexturedCoverageCard
          category={t("you.coverage_benefits.employer_benefits_card_title")}
          status={status}
          title={title}
          subtitle={subtitle}
          fields={fields}
          actionLabel={actionLabel}
          onAction={onPress}
        />
      ) : (
        <>
          <CoverageCardSurface
            category={t("you.coverage_benefits.employer_benefits_card_title")}
            status={status}
            title={title}
            subtitle={subtitle}
            fields={fields}
          />

          {benefitOnFile || noBenefit ? (
            <TextActionButton
              label={actionLabel}
              onPress={onPress}
              color={colors.warm}
              style={{ alignSelf: "flex-start" }}
            />
          ) : (
            <BrownButton label={actionLabel} onPress={onPress} />
          )}
        </>
      )}
    </View>
  );
}
